import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "dropdown", "spinner"]
  static values = {
    location: String,
    locale: String,
    baseText: { type: String, default: "Search " },
    phrases: { type: Array, default: [
      "doctor name...",
      "Cardiology specialist...",
      "Orthopedics care...",
      "Knee Replacement...",
      "Cataract Surgery...",
      "best local hospital...",
      "speciality or procedure..."
    ] },
    fallback: { type: String, default: "Search doctor, hospital, speciality, or procedure..." }
  }

  // Locale path prefix (e.g. "/hi") so dropdown links stay on the visitor's
  // language; empty for the default (English) locale.
  get localePrefix() {
    return this.localeValue ? `/${this.localeValue}` : ""
  }

  connect() {
    this.closeDropdown = this.closeDropdown.bind(this)
    this.onKeydown = this.onKeydown.bind(this)
    
    document.addEventListener("click", this.closeDropdown)
    this.inputTarget.addEventListener("keydown", this.onKeydown)
    
    this.activeIndex = -1
    this.popularSuggestions = [
      { type: "speciality", title: "Cardiology", slug: "cardiology" },
      { type: "speciality", title: "Orthopedics", slug: "orthopedics" },
      { type: "speciality", title: "Oncology", slug: "oncology" },
      { type: "procedure", title: "Knee Replacement", slug: "knee-replacement" },
      { type: "procedure", title: "Cataract Surgery", slug: "cataract-surgery" }
    ]

    this.initTypingPlaceholder()
  }

  disconnect() {
    document.removeEventListener("click", this.closeDropdown)
    this.inputTarget.removeEventListener("keydown", this.onKeydown)
    this.stopTypingPlaceholder()
  }

  initTypingPlaceholder() {
    this.placeholderPhrases = this.phrasesValue
    this.phraseIndex = 0
    this.charIndex = 0
    this.isDeleting = false
    this.typingSpeed = 90
    this.eraseSpeed = 45
    this.delayBetweenPhrases = 2000
    
    this.typePhrase()
  }

  typePhrase() {
    if (!this.hasInputTarget) return

    if (document.activeElement === this.inputTarget || this.inputTarget.value.length > 0) {
      this.typingTimeout = setTimeout(() => this.typePhrase(), 1000)
      return
    }

    const currentPhrase = this.placeholderPhrases[this.phraseIndex]
    const baseText = this.baseTextValue

    if (this.isDeleting) {
      this.charIndex--
    } else {
      this.charIndex++
    }

    const currentText = currentPhrase.substring(0, this.charIndex)
    const displayText = baseText + currentText
    
    const cursor = (Math.floor(Date.now() / 500) % 2 === 0) ? "|" : " "
    this.inputTarget.setAttribute("placeholder", displayText + cursor)

    let currentSpeed = this.isDeleting ? this.eraseSpeed : this.typingSpeed

    if (!this.isDeleting && this.charIndex === currentPhrase.length) {
      currentSpeed = this.delayBetweenPhrases
      this.isDeleting = true
    } else if (this.isDeleting && this.charIndex === 0) {
      this.isDeleting = false
      this.phraseIndex = (this.phraseIndex + 1) % this.placeholderPhrases.length
      currentSpeed = 300
    }

    this.typingTimeout = setTimeout(() => this.typePhrase(), currentSpeed)
  }

  stopTypingPlaceholder() {
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout)
      this.typingTimeout = null
    }
    if (this.hasInputTarget) {
      this.inputTarget.setAttribute("placeholder", this.fallbackValue)
    }
  }

  scrollToSearch() {
    const navbar = document.querySelector("header") || document.querySelector(".sticky") || document.querySelector("nav")
    const navbarHeight = navbar ? navbar.offsetHeight : 80
    const extraGap = 24
    
    const searchCard = this.element.closest(".rounded-2xl") || this.element
    
    const rect = searchCard.getBoundingClientRect()
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop
    const targetY = rect.top + scrollTop - navbarHeight - extraGap
    
    window.scrollTo({
      top: targetY,
      behavior: "smooth"
    })
  }

  onFocus() {
    this.stopTypingPlaceholder()
    this.scrollToSearch()
    const query = this.inputTarget.value.trim()
    if (query.length === 0) {
      this.renderPopularSuggestions()
    } else {
      this.search()
    }
  }

  onBlur(event) {
    setTimeout(() => {
      if (!this.element.contains(document.activeElement)) {
        this.dropdownTarget.classList.add("hidden")
        if (this.inputTarget.value.trim().length === 0) {
          this.initTypingPlaceholder()
        }
      }
    }, 250)
  }

  closeDropdown(event) {
    if (!this.element.contains(event.target)) {
      this.dropdownTarget.classList.add("hidden")
      this.inputTarget.value = ""
      this.activeIndex = -1
    }
  }

  onKeydown(event) {
    // Block submitting an empty/whitespace-only query (no blank results page).
    if (event.key === "Enter" && this.inputTarget.value.trim().length === 0) {
      event.preventDefault()
      return
    }

    if (this.dropdownTarget.classList.contains("hidden")) return

    const links = this.dropdownTarget.querySelectorAll("a")
    if (links.length === 0) return

    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.activeIndex++
      if (this.activeIndex >= links.length) this.activeIndex = 0
      this.highlightActive(links)
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this.activeIndex--
      if (this.activeIndex < 0) this.activeIndex = links.length - 1
      this.highlightActive(links)
    } else if (event.key === "Enter") {
      if (this.activeIndex >= 0 && this.activeIndex < links.length) {
        event.preventDefault()
        links[this.activeIndex].click()
      }
    } else if (event.key === "Escape") {
      this.dropdownTarget.classList.add("hidden")
      this.inputTarget.blur()
    }
  }

  highlightActive(links) {
    links.forEach((link, idx) => {
      if (idx === this.activeIndex) {
        link.classList.add("bg-surface-soft", "ring-1", "ring-primary/20")
        link.scrollIntoView({ block: "nearest", behavior: "smooth" })
      } else {
        link.classList.remove("bg-surface-soft", "ring-1", "ring-primary/20")
      }
    })
  }

  async search() {
    const query = this.inputTarget.value.trim()
    if (query.length < 2) {
      if (query.length === 0) {
        this.renderPopularSuggestions()
      } else {
        this.dropdownTarget.classList.add("hidden")
      }
      return
    }

    this.spinnerTarget.classList.remove("hidden")

    try {
      const response = await fetch(`/api/v1/search?query=${encodeURIComponent(query)}&location=${encodeURIComponent(this.locationValue)}`)
      if (!response.ok) throw new Error("Search failed")
      const data = await response.json()
      this.renderResults(data, query)
    } catch (error) {
      console.error(error)
    } finally {
      this.spinnerTarget.classList.add("hidden")
    }
  }

  renderPopularSuggestions() {
    this.activeIndex = -1
    this.dropdownTarget.classList.remove("hidden")
    
    let html = `
      <div class="p-2">
        <div class="font-sans text-[10px] font-bold uppercase tracking-wider text-primary mb-2 px-2">Popular Specialities & Procedures</div>
        <div class="flex flex-col gap-1">
    `

    this.popularSuggestions.forEach(item => {
      const url = item.type === "speciality"
        ? `${this.localePrefix}/${this.locationValue}/doctors/${item.slug}`
        : `${this.localePrefix}/${this.locationValue}/procedures/${item.slug}`

      html += `
        <a href="${url}" class="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-ink hover:bg-surface-soft transition-colors font-medium">
          <svg class="h-4 w-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          ${item.title}
        </a>
      `
    })

    html += `
        </div>
      </div>
    `
    this.dropdownTarget.innerHTML = html
  }

  renderResults(data, query) {
    this.activeIndex = -1
    const hasDoctors = data.doctors && data.doctors.length > 0
    const hasHospitals = data.hospitals && data.hospitals.length > 0
    const hasSpecialities = data.specialities && data.specialities.length > 0
    const hasProcedures = data.procedures && data.procedures.length > 0

    if (!hasDoctors && !hasHospitals && !hasSpecialities && !hasProcedures) {
      this.dropdownTarget.innerHTML = `
        <div class="p-4 text-center text-sm text-muted">
          No results found for "<span class="font-semibold text-ink">${this.escapeHtml(query)}</span>" in ${this.escapeHtml(this.locationValue)}
        </div>
      `
      this.dropdownTarget.classList.remove("hidden")
      return
    }

    this.dropdownTarget.classList.remove("hidden")
    let html = ""

    if (hasSpecialities) {
      html += `
        <div class="mb-3">
          <div class="font-sans text-[10px] font-bold uppercase tracking-wider text-primary mb-1.5 px-3">Specialities</div>
          <div class="flex flex-col gap-0.5">
      `
      data.specialities.forEach(s => {
        html += `
          <a href="${this.localePrefix}/${this.locationValue}/doctors/${s.slug}" class="flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm text-ink hover:bg-surface-soft transition-colors font-medium">
            <svg class="h-4 w-4 text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 002-2h2a2 2 0 002-2" />
            </svg>
            ${this.highlightMatch(s.title, query)}
          </a>
        `
      })
      html += `</div></div>`
    }

    if (hasProcedures) {
      html += `
        <div class="mb-3">
          <div class="font-sans text-[10px] font-bold uppercase tracking-wider text-primary mb-1.5 px-3">Procedures & Treatments</div>
          <div class="flex flex-col gap-0.5">
      `
      data.procedures.forEach(p => {
        html += `
          <a href="${this.localePrefix}/${this.locationValue}/procedures/${p.slug}" class="flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm text-ink hover:bg-surface-soft transition-colors font-medium">
            <svg class="h-4 w-4 text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 9.172V5L8 4z" />
            </svg>
            ${this.highlightMatch(p.title, query)}
          </a>
        `
      })
      html += `</div></div>`
    }

    if (hasDoctors) {
      html += `
        <div class="mb-3">
          <div class="font-sans text-[10px] font-bold uppercase tracking-wider text-primary mb-1.5 px-3">Doctors</div>
          <div class="flex flex-col gap-0.5">
      `
      data.doctors.forEach(d => {
        const doc_img = d.photo_url 
          ? `<img src="${d.photo_url}" class="h-6 w-6 rounded-full object-cover border border-hairline-soft" alt="">`
          : `<div class="h-6 w-6 rounded-full bg-primary-soft text-primary flex items-center justify-center text-[10px] font-bold">DR</div>`

        html += `
          <a href="${this.localePrefix}/${d.city_slug || this.locationValue}/doctor/${d.slug}" class="flex items-center gap-2.5 rounded-lg px-3 py-1.5 text-sm text-ink hover:bg-surface-soft transition-colors">
            ${doc_img}
            <div class="flex flex-col min-w-0">
              <span class="font-semibold text-ink truncate">${this.highlightMatch(d.fullname, query)}</span>
              <span class="text-xs text-muted truncate">${d.speciality} • ${d.experience_years} yrs exp</span>
            </div>
          </a>
        `
      })
      html += `</div></div>`
    }

    if (hasHospitals) {
      html += `
        <div class="mb-1">
          <div class="font-sans text-[10px] font-bold uppercase tracking-wider text-primary mb-1.5 px-3">Hospitals</div>
          <div class="flex flex-col gap-0.5">
      `
      data.hospitals.forEach(h => {
        html += `
          <a href="${this.localePrefix}/${h.city_slug || this.locationValue}/hospital/${h.slug}" class="flex items-center gap-2.5 rounded-lg px-3 py-1.5 text-sm text-ink hover:bg-surface-soft transition-colors">
            <svg class="h-4 w-4 text-muted flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <div class="flex flex-col min-w-0">
              <span class="font-semibold text-ink truncate">${this.highlightMatch(h.title, query)}</span>
              <span class="text-xs text-muted truncate">${h.city || ""}</span>
            </div>
          </a>
        `
      })
      html += `</div></div>`
    }

    this.dropdownTarget.innerHTML = html
  }

  highlightMatch(text, query) {
    if (!text) return ""
    const idx = text.toLowerCase().indexOf(query.toLowerCase())
    if (idx === -1) return this.escapeHtml(text)
    
    const before = text.substring(0, idx)
    const match = text.substring(idx, idx + query.length)
    const after = text.substring(idx + query.length)

    return `${this.escapeHtml(before)}<mark class="bg-primary/10 text-primary rounded px-0.5 font-bold">${this.escapeHtml(match)}</mark>${this.escapeHtml(after)}`
  }

  escapeHtml(string) {
    const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    }
    return String(string).replace(/[&<>"']/g, function(m) { return map[m]; })
  }
}
